import {jsPDF} from 'jspdf'
import autoTable, {RowInput} from 'jspdf-autotable'
import moment from 'moment'
import {
  OrbitReport,
  OrbitReportDailyCount,
  OrbitReportEventCount,
  OrbitReportPeriod,
  OrbitReportTransaction,
} from '@/model/resource/OrbitReport'
import {OrbitNetworkHelper} from '@/helpers/OrbitNetworkHelper'

type Rgb = [number, number, number]
type PeriodMetric = Exclude<keyof OrbitReportPeriod, 'startsAt' | 'endsAt'>

const PAGE_MARGIN = 14
const BRAND_COLOR: Rgb = [0, 166, 126]
const PREVIOUS_COLOR: Rgb = [176, 186, 197]
const TEXT_COLOR: Rgb = [33, 41, 54]
const MUTED_COLOR: Rgb = [110, 120, 135]
const GRID_COLOR: Rgb = [228, 232, 238]

interface ProjectTotals {
  projectTitle: string
  networkIdentifier: string | null
  eventNames: Set<string>
  currentMonthCount: number
  previousMonthCount: number
}

interface EventTotals {
  eventname: string
  projects: Set<string>
  currentMonthCount: number
  previousMonthCount: number
}

export class OrbitReportPdfHelper {
  private readonly doc = new jsPDF({unit: 'mm', format: 'a4'})
  private readonly pageWidth = this.doc.internal.pageSize.getWidth()
  private readonly pageHeight = this.doc.internal.pageSize.getHeight()
  private readonly contentWidth = this.pageWidth - PAGE_MARGIN * 2
  private cursorY = PAGE_MARGIN
  private readonly report: OrbitReport

  constructor(report: OrbitReport) {
    this.report = report
  }

  static download(report: OrbitReport) {
    new OrbitReportPdfHelper(report).build().save()
  }

  build() {
    this.drawHeader()
    this.drawSummaryTable()
    this.drawDailyChart()
    this.drawEventsByProjectTable()
    this.drawEventTotalsTable()
    this.drawProjectTotalsTable()
    this.drawRoundsTable()
    this.drawMethodologyNotes()
    this.drawTransactionDetails()
    this.drawFooters()

    return this
  }

  save() {
    const month = this.englishMoment(this.report.currentMonth.startsAt)
    const network = (this.report.networkIdentifier ?? 'all-networks').replace(
      /\//g,
      '-'
    )

    this.doc.save(`orbit-report-${network}-${month.format('YYYY-MM')}.pdf`)
  }

  private get previousLabel() {
    return this.monthLabel(this.report.previousMonth)
  }

  private get currentLabel() {
    return `${this.monthLabel(this.report.currentMonth)} (to date)`
  }

  private get networkLabel() {
    return this.report.networkIdentifier
      ? OrbitNetworkHelper.label(this.report.networkIdentifier)
      : 'All networks'
  }

  private drawHeader() {
    const bandHeight = 30

    this.doc.setFillColor(...BRAND_COLOR)
    this.doc.rect(0, 0, this.pageWidth, bandHeight, 'F')

    this.doc.setTextColor(255, 255, 255)
    this.doc.setFont('helvetica', 'bold')
    this.doc.setFontSize(20)
    this.doc.text('Orbit Network Report', PAGE_MARGIN, 14)

    this.doc.setFont('helvetica', 'normal')
    this.doc.setFontSize(10)
    this.doc.text(
      `${this.networkLabel}  |  ${this.monthLabel(
        this.report.previousMonth
      )} vs ${this.monthLabel(this.report.currentMonth)}`,
      PAGE_MARGIN,
      22
    )

    const generatedAt = this.englishMoment(this.report.generatedAt).format(
      'MMM D, YYYY HH:mm'
    )
    this.doc.text(
      `Generated ${generatedAt}`,
      this.pageWidth - PAGE_MARGIN,
      22,
      {
        align: 'right',
      }
    )

    this.cursorY = bandHeight + 10
  }

  private drawSummaryTable() {
    const previous = this.report.previousMonth
    const current = this.report.currentMonth

    const metric = (
      label: string,
      key: PeriodMetric,
      decimals = 0
    ): RowInput => [
      label,
      this.formatNumber(previous[key], decimals),
      this.formatNumber(current[key], decimals),
      this.formatChange(previous[key], current[key]),
    ]

    this.drawSectionTitle('Network overview')

    this.drawTable(
      [['Metric', this.previousLabel, this.currentLabel, 'Change']],
      [
        metric('Tracked events', 'totalEvents'),
        metric('Unique transactions', 'uniqueTransactions'),
        metric('Active addresses', 'uniqueAddresses'),
        metric('New addresses (first Orbit activity)', 'newAddresses'),
        metric('Projects with activity', 'activeProjects'),
        metric('Rounds with activity', 'activeRounds'),
        metric('Rewards approved', 'approvedRewards'),
        metric('Rewards claimed', 'claimedRewards'),
        metric('Rewarded addresses', 'rewardedAddresses'),
        metric('GAS claimed', 'claimedGas', 2),
      ],
      1,
      {0: {cellWidth: 70}}
    )
  }

  private drawDailyChart() {
    const chartHeight = 55
    const titleHeight = 20

    this.ensureSpace(chartHeight + titleHeight + 10)
    this.drawSectionTitle('Daily tracked events')
    this.cursorY += 4

    const previousByDay = this.eventsByDayOfMonth(
      this.report.previousMonth.startsAt
    )
    const currentByDay = this.eventsByDayOfMonth(
      this.report.currentMonth.startsAt
    )

    const axisWidth = 12
    const chartX = PAGE_MARGIN + axisWidth
    const chartWidth = this.contentWidth - axisWidth
    const chartTop = this.cursorY
    const chartBottom = chartTop + chartHeight
    const maxValue = this.niceMax(
      Math.max(1, ...previousByDay, ...currentByDay)
    )
    const gridLines = 4

    this.doc.setFontSize(7)
    this.doc.setFont('helvetica', 'normal')
    this.doc.setLineWidth(0.2)

    for (let i = 0; i <= gridLines; i++) {
      const value = (maxValue / gridLines) * i
      const y = chartBottom - (chartHeight * i) / gridLines

      this.doc.setDrawColor(...GRID_COLOR)
      this.doc.line(chartX, y, chartX + chartWidth, y)
      this.doc.setTextColor(...MUTED_COLOR)
      this.doc.text(this.formatNumber(value), chartX - 2, y + 1, {
        align: 'right',
      })
    }

    const days = 31
    const slotWidth = chartWidth / days
    const barWidth = slotWidth * 0.36

    for (let day = 0; day < days; day++) {
      const slotX = chartX + slotWidth * day + slotWidth * 0.14

      this.drawBar(
        slotX,
        chartBottom,
        barWidth,
        chartHeight,
        previousByDay[day],
        maxValue,
        PREVIOUS_COLOR
      )
      this.drawBar(
        slotX + barWidth,
        chartBottom,
        barWidth,
        chartHeight,
        currentByDay[day],
        maxValue,
        BRAND_COLOR
      )

      if (day % 2 === 0) {
        this.doc.setTextColor(...MUTED_COLOR)
        this.doc.text(
          String(day + 1),
          chartX + slotWidth * (day + 0.5),
          chartBottom + 4,
          {
            align: 'center',
          }
        )
      }
    }

    const legendY = chartBottom + 10
    this.drawLegendItem(
      chartX,
      legendY,
      PREVIOUS_COLOR,
      this.monthLabel(this.report.previousMonth)
    )
    this.drawLegendItem(
      chartX + 45,
      legendY,
      BRAND_COLOR,
      this.monthLabel(this.report.currentMonth)
    )

    this.doc.setTextColor(...MUTED_COLOR)
    this.doc.text('Day of month', chartX + chartWidth, legendY, {
      align: 'right',
    })

    this.cursorY = legendY + 10
  }

  private drawEventsByProjectTable() {
    this.drawSectionTitle('Events by project')

    const rows: RowInput[] = this.report.events.map(event => [
      this.projectName(event),
      OrbitNetworkHelper.label(event.networkIdentifier),
      event.eventname ?? '-',
      this.formatNumber(event.previousMonthCount),
      this.formatNumber(event.currentMonthCount),
      this.formatChange(event.previousMonthCount, event.currentMonthCount),
      `${this.formatNumber(event.previousMonthAddresses)} / ${this.formatNumber(
        event.currentMonthAddresses
      )}`,
    ])

    this.drawTable(
      [
        [
          'Project',
          'Network',
          'Event',
          'Prev. month',
          'This month',
          'Change',
          'Addresses (prev. / this)',
        ],
      ],
      rows.length ? rows : [this.emptyRow(7)],
      3,
      {0: {cellWidth: 38}, 2: {cellWidth: 30}}
    )
  }

  private drawEventTotalsTable() {
    const totals = new Map<string, EventTotals>()

    this.report.events.forEach(event => {
      const eventname = event.eventname ?? '-'
      const entry = totals.get(eventname) ?? {
        eventname,
        projects: new Set<string>(),
        currentMonthCount: 0,
        previousMonthCount: 0,
      }

      entry.projects.add(this.projectName(event))
      entry.currentMonthCount += event.currentMonthCount
      entry.previousMonthCount += event.previousMonthCount
      totals.set(eventname, entry)
    })

    const rows: RowInput[] = Array.from(totals.values())
      .sort((a, b) => b.currentMonthCount - a.currentMonthCount)
      .map(entry => [
        entry.eventname,
        this.formatNumber(entry.projects.size),
        this.formatNumber(entry.previousMonthCount),
        this.formatNumber(entry.currentMonthCount),
        this.formatChange(entry.previousMonthCount, entry.currentMonthCount),
      ])

    this.drawSectionTitle('Totals by event')

    this.drawTable(
      [['Event', 'Projects', 'Prev. month', 'This month', 'Change']],
      rows.length ? rows : [this.emptyRow(5)],
      1
    )
  }

  private drawProjectTotalsTable() {
    const totals = new Map<string, ProjectTotals>()

    this.report.events.forEach(event => {
      const key = `${event.idContract}|${event.networkIdentifier}`
      const entry = totals.get(key) ?? {
        projectTitle: this.projectName(event),
        networkIdentifier: event.networkIdentifier,
        eventNames: new Set<string>(),
        currentMonthCount: 0,
        previousMonthCount: 0,
      }

      entry.eventNames.add(event.eventname ?? '-')
      entry.currentMonthCount += event.currentMonthCount
      entry.previousMonthCount += event.previousMonthCount
      totals.set(key, entry)
    })

    const rows: RowInput[] = Array.from(totals.values())
      .sort((a, b) => b.currentMonthCount - a.currentMonthCount)
      .map(entry => [
        entry.projectTitle,
        OrbitNetworkHelper.label(entry.networkIdentifier),
        this.formatNumber(entry.eventNames.size),
        this.formatNumber(entry.previousMonthCount),
        this.formatNumber(entry.currentMonthCount),
        this.formatChange(entry.previousMonthCount, entry.currentMonthCount),
      ])

    this.drawSectionTitle('Totals by project')

    this.drawTable(
      [
        [
          'Project',
          'Network',
          'Events tracked',
          'Prev. month',
          'This month',
          'Change',
        ],
      ],
      rows.length ? rows : [this.emptyRow(6)],
      2,
      {0: {cellWidth: 50}}
    )
  }

  private drawRoundsTable() {
    const rows: RowInput[] = this.report.rounds.map(round => [
      `#${round.round ?? '-'}`,
      OrbitNetworkHelper.label(round.networkIdentifier),
      this.formatDate(round.startsAt),
      this.formatDate(round.endsAt),
      round.isRunning ? 'Running' : 'Closed',
      this.formatNumber(round.projectCount),
      this.formatNumber(round.totalEvents),
      this.formatNumber(round.participants),
      this.formatNumber(round.claimedRewards),
    ])

    this.drawSectionTitle('Rounds in the period')

    this.drawTable(
      [
        [
          'Round',
          'Network',
          'Starts',
          'Ends',
          'Status',
          'Projects',
          'Events',
          'Participants',
          'Claims',
        ],
      ],
      rows.length ? rows : [this.emptyRow(9)],
      5
    )
  }

  private drawMethodologyNotes() {
    const notes = [
      'Events are the on-chain notifications Orbit counts for each funded project, dated by their block time.',
      `"${this.currentLabel}" covers the first day of the month until the report was generated.`,
      'Active addresses are wallets with at least one tracked event in the month; new addresses had their first Orbit event in that month.',
      'Reward approvals, claims and claimed GAS are dated by their last status change.',
      'Round totals cover the whole round, not only the reported months.',
    ]

    this.ensureSpace(40)
    this.drawSectionTitle('Notes')

    this.doc.setFont('helvetica', 'normal')
    this.doc.setFontSize(8)
    this.doc.setTextColor(...MUTED_COLOR)

    notes.forEach(note => {
      const lines = this.doc.splitTextToSize(`- ${note}`, this.contentWidth)
      this.ensureSpace(lines.length * 4)
      this.doc.text(lines, PAGE_MARGIN, this.cursorY)
      this.cursorY += lines.length * 4
    })
  }

  private drawTransactionDetails() {
    this.doc.addPage()
    this.cursorY = PAGE_MARGIN + 6

    this.drawSectionTitle('Transaction details')

    this.doc.setFont('helvetica', 'normal')
    this.doc.setFontSize(8)
    this.doc.setTextColor(...MUTED_COLOR)
    this.doc.text(
      `Every tracked event in the period: ${this.formatNumber(
        this.report.transactions.length
      )} rows. A transaction emitting several tracked events appears once per event.`,
      PAGE_MARGIN,
      this.cursorY + 2
    )
    this.cursorY += 8

    const currentMonthStart = this.toDate(
      this.report.currentMonth.startsAt
    ).getTime()
    const isPreviousMonth = (it: OrbitReportTransaction) =>
      this.toDate(it.transactionDate).getTime() < currentMonthStart

    this.drawMonthTransactions(
      this.previousLabel,
      this.report.transactions.filter(it => isPreviousMonth(it))
    )
    this.drawMonthTransactions(
      this.currentLabel,
      this.report.transactions.filter(it => !isPreviousMonth(it))
    )
  }

  private drawMonthTransactions(
    label: string,
    transactions: OrbitReportTransaction[]
  ) {
    const fontSize = 6
    const cellPadding = 1
    const showNetwork = !this.report.networkIdentifier

    const head = ['Date', 'Project', 'Event', 'Address', 'Transaction hash']

    const rows: RowInput[] = transactions.map(it => {
      const project = it.projectTitle || it.contractHash || '-'
      const network = OrbitNetworkHelper.label(it.networkIdentifier)

      return [
        this.englishMoment(it.transactionDate).format('YYYY-MM-DD HH:mm'),
        showNetwork
          ? `${project}
${network}`
          : project,
        it.eventname ?? '-',
        it.address ?? '-',
        it.transactionHash ?? '-',
      ]
    })

    this.doc.setFont('helvetica', 'normal')
    this.doc.setFontSize(fontSize)

    const fitWidth = (values: Array<string | null>) =>
      values.reduce(
        (widest, it) => Math.max(widest, this.doc.getTextWidth(it ?? '-')),
        0
      ) +
      cellPadding * 2 +
      0.5

    const dateWidth = fitWidth(['0000-00-00 00:00'])
    const addressWidth = fitWidth([
      'Address',
      ...transactions.map(it => it.address),
    ])
    const hashWidth = fitWidth([
      'Transaction hash',
      ...transactions.map(it => it.transactionHash),
    ])

    this.ensureSpace(30)
    this.doc.setFont('helvetica', 'bold')
    this.doc.setFontSize(10)
    this.doc.setTextColor(...TEXT_COLOR)
    this.doc.text(
      `${label}  (${this.formatNumber(transactions.length)})`,
      PAGE_MARGIN,
      this.cursorY
    )
    this.cursorY += 3

    autoTable(this.doc, {
      head: [head],
      body: rows.length ? rows : [this.emptyRow(head.length)],
      startY: this.cursorY,
      margin: {left: PAGE_MARGIN, right: PAGE_MARGIN, bottom: 16},
      theme: 'striped',
      styles: {
        font: 'helvetica',
        fontSize,
        cellPadding,
        textColor: TEXT_COLOR,
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: BRAND_COLOR,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: {cellWidth: dateWidth},
        3: {cellWidth: addressWidth},
        4: {cellWidth: hashWidth},
      },
    })

    this.cursorY = this.lastTableEndY() + 10
  }

  private drawFooters() {
    const pageCount = this.doc.getNumberOfPages()

    for (let page = 1; page <= pageCount; page++) {
      this.doc.setPage(page)
      this.doc.setFont('helvetica', 'normal')
      this.doc.setFontSize(8)
      this.doc.setTextColor(...MUTED_COLOR)
      this.doc.text(
        `Orbit Network Report  |  ${this.networkLabel}`,
        PAGE_MARGIN,
        this.pageHeight - 8
      )
      this.doc.text(
        `Page ${page} of ${pageCount}`,
        this.pageWidth - PAGE_MARGIN,
        this.pageHeight - 8,
        {align: 'right'}
      )
    }
  }

  private drawSectionTitle(title: string) {
    this.ensureSpace(20)

    this.doc.setFont('helvetica', 'bold')
    this.doc.setFontSize(13)
    this.doc.setTextColor(...TEXT_COLOR)
    this.doc.text(title, PAGE_MARGIN, this.cursorY)

    this.cursorY += 4
  }

  private drawTable(
    head: RowInput[],
    body: RowInput[],
    firstNumericColumn: number,
    columnStyles: Record<number, {cellWidth: number}> = {}
  ) {
    autoTable(this.doc, {
      head,
      body,
      startY: this.cursorY,
      margin: {left: PAGE_MARGIN, right: PAGE_MARGIN, bottom: 16},
      theme: 'striped',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 2,
        textColor: TEXT_COLOR,
      },
      headStyles: {
        fillColor: BRAND_COLOR,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      columnStyles,
      didParseCell: data => {
        const isNumeric = data.column.index >= firstNumericColumn
        const isSingleCell = data.cell.colSpan === 1

        if (isNumeric && isSingleCell) data.cell.styles.halign = 'right'
      },
    })

    this.cursorY = this.lastTableEndY() + 12
  }

  private drawBar(
    x: number,
    bottom: number,
    width: number,
    chartHeight: number,
    value: number,
    maxValue: number,
    color: Rgb
  ) {
    if (!value) return

    const height = (value / maxValue) * chartHeight

    this.doc.setFillColor(...color)
    this.doc.rect(x, bottom - height, width, height, 'F')
  }

  private drawLegendItem(x: number, y: number, color: Rgb, label: string) {
    this.doc.setFillColor(...color)
    this.doc.rect(x, y - 2.5, 3, 3, 'F')
    this.doc.setTextColor(...TEXT_COLOR)
    this.doc.setFontSize(8)
    this.doc.text(label, x + 5, y)
  }

  private ensureSpace(height: number) {
    if (this.cursorY + height <= this.pageHeight - 16) return

    this.doc.addPage()
    this.cursorY = PAGE_MARGIN + 6
  }

  private lastTableEndY(): number {
    const docWithTable = this.doc as jsPDF & {lastAutoTable?: {finalY: number}}

    return docWithTable.lastAutoTable?.finalY ?? this.cursorY
  }

  private eventsByDayOfMonth(monthStart: OrbitReportPeriod['startsAt']) {
    const month = this.englishMoment(monthStart).format('YYYY-MM')
    const values: number[] = new Array(31).fill(0)

    this.report.dailyActivity
      .filter((it: OrbitReportDailyCount) => it.day?.startsWith(month))
      .forEach((it: OrbitReportDailyCount) => {
        const dayIndex = Number(it.day?.slice(8, 10)) - 1
        values[dayIndex] = it.events
      })

    return values
  }

  private niceMax(value: number) {
    const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
    const steps = [1, 2, 2.5, 5, 10]
    const step = steps.find(it => it * magnitude >= value) ?? 10

    return step * magnitude
  }

  private projectName(event: OrbitReportEventCount) {
    return event.projectTitle || event.contractHash || 'Unknown project'
  }

  private monthLabel(period: OrbitReportPeriod) {
    return this.englishMoment(period.startsAt).format('MMMM YYYY')
  }

  private formatDate(value: OrbitReportPeriod['startsAt']) {
    return value ? this.englishMoment(value).format('MMM D, YYYY') : '-'
  }

  private formatNumber(value: number, decimals = 0) {
    return Number(value ?? 0).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
  }

  private formatChange(previous: number, current: number) {
    if (!previous && !current) return '-'
    if (!previous) return 'New'

    const change = ((current - previous) / previous) * 100
    const sign = change > 0 ? '+' : ''

    return `${sign}${change.toFixed(1)}%`
  }

  private emptyRow(columns: number): RowInput {
    return [
      {
        content: 'No activity in the period',
        colSpan: columns,
        styles: {halign: 'center'},
      },
    ]
  }

  private englishMoment(value: OrbitReportPeriod['startsAt']) {
    return moment(this.toDate(value)).locale('en')
  }

  private toDate(value: OrbitReportPeriod['startsAt']) {
    return value ? new Date(value) : new Date()
  }
}
